import { v4 as uuidv4 } from "uuid";
import { getAllProviderUsersByProviderId } from "../api/api-methods";

async function findExistingProviderUser(providerId, email) {
  const normalizedEmail = email?.trim()?.toLowerCase();
  if (!normalizedEmail || !providerId) return null;

  try {
    const usersData = await getAllProviderUsersByProviderId(providerId, 100, 1, "");
    return (
      usersData?.items?.find((u) => u.email?.toLowerCase() === normalizedEmail) ??
      null
    );
  } catch {
    return null;
  }
}

/**
 * Contacts to provision on VQQ request (prod / release-candidate UI: dropdown + additionalContacts).
 */
export function collectLegacyVqqContacts({
  allContacts,
  currentSelectedContact,
  additionalContacts,
  formContact,
}) {
  if (
    currentSelectedContact?.id === "new-contact" &&
    additionalContacts?.length
  ) {
    return additionalContacts.map((contact, index) => ({
      email: contact.email,
      first_name: contact.firstName,
      last_name: contact.lastName,
      job_title: contact.title,
      is_primary: index === 0,
    }));
  }

  if ((allContacts?.length ?? 0) === 0) {
    const first = additionalContacts?.[0];
    if (first?.email?.trim() && first?.firstName?.trim()) {
      return [
        {
          email: first.email,
          first_name: first.firstName,
          last_name: first.lastName,
          job_title: first.title,
          is_primary: true,
        },
      ];
    }

    if (formContact?.email?.trim() && formContact?.first_name?.trim()) {
      return [{ ...formContact, is_primary: true }];
    }
  }

  return [];
}

/**
 * Contacts to provision on VQA request (prod / release-candidate UI).
 */
export function collectLegacyAssessmentContacts(
  currentSelectedContact,
  additionalContacts,
) {
  if (
    currentSelectedContact?.id !== "new-contact" ||
    !additionalContacts?.length
  ) {
    return [];
  }

  return additionalContacts.map((contact, index) => ({
    email: contact.email,
    first_name: contact.firstName,
    last_name: contact.lastName,
    job_title: contact.title,
    is_primary: index === 0,
  }));
}

/**
 * Ad-hoc contacts from Enhancement 10 multi-select (RequestContactInlineForm).
 * Sent in request_contacts but not yet vendor users until POST /Identity/ProviderUsers.
 */
export function collectAdHocContactsFromSelection(
  selectedRequestContacts,
  knownVendorEmails = null,
) {
  const known =
    knownVendorEmails ??
    new Set(
      (selectedRequestContacts ?? [])
        .filter((c) => !(c.isAdHoc ?? c.is_ad_hoc))
        .map((c) => c.email?.trim()?.toLowerCase())
        .filter(Boolean),
    );

  return (selectedRequestContacts ?? [])
    .filter((c) => {
      const email = c.email?.trim()?.toLowerCase();
      if (!email) return false;
      if (c.isAdHoc || c.is_ad_hoc) return true;
      return knownVendorEmails != null && !known.has(email);
    })
    .map((contact) => ({
      email: contact.email,
      first_name: contact.first_name ?? contact.firstName ?? "",
      last_name: contact.last_name ?? contact.lastName ?? "",
      job_title:
        contact.job_title ?? contact.title ?? contact.displayTitle ?? "",
      is_primary: Boolean(
        contact.is_request_primary ?? contact.isCategoryPrimary,
      ),
    }))
    .filter((c) => c.email?.trim() && (c.first_name ?? "").trim());
}

/**
 * Contacts to provision before VQQ submit — legacy dropdown + Enhancement 10 chips
 * (ad-hoc and existing selected contacts that may lack Clerk/HC).
 */
export function collectVqqContactsToProvision({
  selectedRequestContacts,
  allContacts,
  currentSelectedContact,
  additionalContacts,
  formContact,
  knownVendorEmails,
}) {
  const legacy = collectLegacyVqqContacts({
    allContacts,
    currentSelectedContact,
    additionalContacts,
    formContact,
  });
  const adHoc = collectAdHocContactsFromSelection(
    selectedRequestContacts,
    knownVendorEmails,
  );
  const selected = collectSelectedContactsForEnsure(selectedRequestContacts);

  const byEmail = new Map();
  [...legacy, ...adHoc, ...selected].forEach((contact) => {
    const email = contact.email?.trim()?.toLowerCase();
    if (!email) return;
    byEmail.set(email, { ...contact, email });
  });
  return Array.from(byEmail.values());
}

/**
 * Contacts to provision before VQA submit — legacy dropdown + Enhancement 10 chips
 * (ad-hoc and existing selected contacts that may lack Clerk/HC).
 */
export function collectAssessmentContactsToProvision({
  selectedRequestContacts,
  currentSelectedContact,
  additionalContacts,
  knownVendorEmails,
}) {
  const legacy = collectLegacyAssessmentContacts(
    currentSelectedContact,
    additionalContacts,
  );
  const adHoc = collectAdHocContactsFromSelection(
    selectedRequestContacts,
    knownVendorEmails,
  );
  const selected = collectSelectedContactsForEnsure(selectedRequestContacts);

  const byEmail = new Map();
  [...legacy, ...adHoc, ...selected].forEach((contact) => {
    const email = contact.email?.trim()?.toLowerCase();
    if (!email) return;
    byEmail.set(email, { ...contact, email });
  });
  return Array.from(byEmail.values());
}

function hasClerkId(providerUser) {
  return Boolean(
    providerUser?.clerk_id?.trim?.() ||
      providerUser?.provider_user_clerk_id?.trim?.() ||
      providerUser?.clerkId?.trim?.(),
  );
}

/**
 * True when Identity "already exists" means the contact is already onboarded
 * enough for request flows (HC invite / Diligent email still use the address).
 */
function isIdentityAlreadyExistsError(error) {
  const message = String(
    error?.response?.data?.message ??
      error?.response?.data ??
      error?.message ??
      "",
  ).toLowerCase();
  return message.includes("already exists");
}

/**
 * Ensures contacts have Identity + HC + Clerk accounts when missing.
 * - New emails → POST /Identity/ProviderUsers (creates HC user).
 * - Existing ProviderUser without clerk_id → still attempts Identity create;
 *   if AspNetUsers already has the email, treats as provisioned (backend
 *   VQQ send / Request-Contact also force HC org membership).
 */
export async function ensureLegacyRequestContacts({
  contacts,
  providerId,
  organization_id,
  providerName,
  providerDataRoles,
  addProviderContact,
}) {
  if (!contacts?.length || !providerId || !organization_id) {
    return;
  }

  await Promise.all(
    contacts.map(async (contact) => {
      const email = contact.email?.trim()?.toLowerCase();
      const first_name = (contact.first_name ?? contact.firstName ?? "").trim();
      if (!email || !first_name) {
        return;
      }

      const existing = await findExistingProviderUser(providerId, email);
      if (existing?.provider_user_id && hasClerkId(existing)) {
        return;
      }

      try {
        await addProviderContact({
          provider_user_id: existing?.provider_user_id ?? uuidv4(),
          provider_id: providerId,
          organization_id,
          email,
          first_name,
          last_name:
            (contact.last_name ?? contact.lastName ?? "").trim() || "lastname",
          job_title: contact.job_title ?? contact.title ?? "",
          is_primary: Boolean(contact.is_primary),
          provider_name: providerName || "",
          // Skip ProviderService Users/Create when the catalog row already exists
          // so Identity can still create HC + Clerk for incomplete contacts.
          ...(existing?.provider_user_id
            ? { req_from_intra_service: true }
            : {}),
          ...providerDataRoles,
        });
      } catch (error) {
        if (isIdentityAlreadyExistsError(error)) {
          return;
        }
        throw error;
      }
    }),
  );
}

/**
 * All selected request-contact chips (existing + ad-hoc) for pre-submit ensure.
 */
export function collectSelectedContactsForEnsure(selectedRequestContacts) {
  return (selectedRequestContacts ?? [])
    .map((contact) => ({
      email: contact.email,
      first_name: contact.first_name ?? contact.firstName ?? "",
      last_name: contact.last_name ?? contact.lastName ?? "",
      job_title:
        contact.job_title ?? contact.title ?? contact.displayTitle ?? "",
      is_primary: Boolean(
        contact.is_request_primary ?? contact.isCategoryPrimary,
      ),
    }))
    .filter((c) => c.email?.trim() && (c.first_name ?? "").trim());
}
